import { useState } from "react";
import type { DivulgacaoStatus } from "@foto/shared";
import { trpc } from "../../lib/trpc.js";
import { Botao, Campo, Chave, Entrada, Falha } from "../../components/ui.js";

/**
 * Edição de uma divulgação.
 *
 * `divulgacoes.update` existia desde a Fase 7 e nunca teve tela: título, duração
 * e ativo/inativo só podiam ser mudados por SQL. Na prática isso significava que
 * toda peça passava os 8 segundos do padrão, sem exceção.
 */
export function EditorDivulgacao({
  id,
  titulo,
  duracao,
  status,
  ehVideo,
  aoFechar,
}: {
  id: number;
  titulo: string | null;
  duracao: number;
  status: DivulgacaoStatus;
  ehVideo: boolean;
  aoFechar: () => void;
}) {
  const utils = trpc.useUtils();
  const [t, setT] = useState(titulo ?? "");
  const [d, setD] = useState(String(duracao));
  const [ativa, setAtiva] = useState(status === "active");
  const [erro, setErro] = useState<string | null>(null);

  const salvar = trpc.divulgacoes.update.useMutation({
    onSuccess: () => {
      void utils.divulgacoes.list.invalidate();
      aoFechar();
    },
    onError: (e) => setErro(e.message),
  });

  const segundos = Number(d);
  const duracaoValida = Number.isInteger(segundos) && segundos >= 1 && segundos <= 120;

  return (
    <div className="flex flex-col gap-3">
      <Campo rotulo="Título">
        <Entrada value={t} onChange={(e) => setT(e.target.value)} placeholder="Opcional" />
      </Campo>

      <Campo
        rotulo="Segundos na tela"
        dica={
          ehVideo
            ? "Vídeo toca até o fim; este valor só vale como limite se o arquivo travar."
            : "Entre 1 e 120."
        }
        erro={d !== "" && !duracaoValida ? "Informe um número entre 1 e 120." : null}
      >
        <Entrada
          type="number"
          inputMode="numeric"
          min={1}
          max={120}
          value={d}
          onChange={(e) => setD(e.target.value)}
        />
      </Campo>

      <Campo rotulo="Ativa" dica="Desativada, some da galeria sem ser excluída.">
        <Chave ligada={ativa} aoAlternar={setAtiva} rotulo="Divulgação ativa" />
      </Campo>

      {erro && <Falha mensagem={erro} />}

      <div className="flex gap-2">
        <Botao
          tom="brasa"
          onClick={() => {
            setErro(null);
            salvar.mutate({
              id,
              title: t.trim() === "" ? null : t.trim(),
              durationSeconds: segundos,
              status: ativa ? "active" : "inactive",
            });
          }}
          disabled={!duracaoValida}
          carregando={salvar.isPending}
        >
          Salvar
        </Botao>
        <Botao tom="quieto" onClick={aoFechar} disabled={salvar.isPending}>
          Cancelar
        </Botao>
      </div>
    </div>
  );
}
